import React from 'react'
import moment, { Moment } from 'moment'
import { AutocompleteResult } from 'core-system/Map/AddressAutocomplete/AddressAutocompleteField'
import {
  AddTripPayload,
  Trip,
  TripLocation,
} from 'redux/employeePlatform/employeePlatformTypes'
import * as ModeTypeIcons from 'core-system/Icons/modeTypes'

export const tripPurposes = new Map([
  ['COMMUTE', 'Commute'],
  ['ERRAND_GROCERIES', 'Errand / groceries'],
  ['CHILDCARE', 'Childcare'],
  ['LEISURE', 'Leisure'],
  ['OTHER', 'Other'],
])

export const tripModeTypes = {
  BIKE: {
    baseId: 'BIKE',
    name: 'Bike',
    icon: <ModeTypeIcons.Bike />,
    hasProviders: false,
  },
  BIKESHARE: {
    baseId: 'BIKESHARE',
    name: 'Bikeshare',
    icon: <ModeTypeIcons.Bike />,
    hasProviders: true,
  },
  BUS: {
    baseId: 'BUS',
    name: 'Bus',
    icon: <ModeTypeIcons.Bus />,
    hasProviders: true,
  },
  CAR: {
    baseId: 'CAR',
    name: 'Car',
    icon: <ModeTypeIcons.Car />,
    hasProviders: false,
  },
  CARPOOL: {
    baseId: 'CARPOOL',
    name: 'Carpool',
    icon: <ModeTypeIcons.Carpool />,
    hasProviders: true,
  },
  CARSHARE: {
    baseId: 'CARSHARE',
    name: 'Carshare',
    icon: <ModeTypeIcons.Car />,
    hasProviders: true,
  },
  SCOOTER: {
    baseId: 'SCOOTER',
    name: 'Scooter',
    icon: <ModeTypeIcons.Scooter />,
    hasProviders: true,
  },
  SCOOTERSHARE: {
    baseId: 'SCOOTERSHARE',
    name: 'Scootershare',
    icon: <ModeTypeIcons.ElectricScooter />,
    hasProviders: true,
  },
  MOPED: {
    baseId: 'MOPED',
    name: 'Moped',
    icon: <ModeTypeIcons.Moped />,
    hasProviders: true,
  },
  MOPEDSHARE: {
    baseId: 'MOPEDSHARE',
    name: 'Mopedshare',
    icon: <ModeTypeIcons.ElectricMoped />,
    hasProviders: true,
  },
  RIDESHARE: {
    baseId: 'RIDESHARE',
    name: 'Rideshare',
    icon: <ModeTypeIcons.Rideshare />,
    hasProviders: true,
  },
  TRANSIT: {
    baseId: 'TRANSIT',
    name: 'Transit',
    icon: <ModeTypeIcons.Transit />,
    hasProviders: true,
  },
  WALK: {
    baseId: 'WALK',
    name: 'Walk',
    icon: <ModeTypeIcons.Walking />,
    hasProviders: false,
  },
  SHUTTLE: {
    baseId: 'SHUTTLE',
    name: 'Shuttle',
    icon: <ModeTypeIcons.Van />,
    hasProviders: true,
  },
  VANPOOL: {
    baseId: 'VANPOOL',
    name: 'Vanpool',
    icon: <ModeTypeIcons.Vanpool />,
    hasProviders: true,
  },
  FERRY: {
    baseId: 'FERRY',
    name: 'Ferry',
    icon: <ModeTypeIcons.Ferry />,
    hasProviders: true,
  },
  COMMRAIL: {
    baseId: 'COMMRAIL',
    name: 'Rail',
    icon: <ModeTypeIcons.Transit />,
    hasProviders: true,
  },
  LITERAIL: {
    baseId: 'LITERAIL',
    name: 'Literail',
    icon: <ModeTypeIcons.Transit />,
    hasProviders: true,
  },
}

export const NoTripReasonDropDownItems = [
  { id: 'WFH', text: 'Work From Home' },
  { id: 'VACATION', text: 'Vacation' },
  { id: 'SICK', text: 'Sick' },
  { id: 'OTHER', text: 'Other' },
]

export interface TripDateParts {
  day: string
  month: string
  year: string
}

export interface TripFormLocationState {
  day?: number
  month?: number
  year?: number
  trip?: Trip
}

export const isFutureDate = (date: Moment) => {
  return date.isAfter(moment())
}

export const formatTripLocation = (location: AutocompleteResult) => {
  return {
    address: location.address,
    lat: location.coordinates[1],
    lng: location.coordinates[0],
    home: location.type === 'HOME',
    worksite: location.type === 'WORK',
  }
}

export const savedTripLocation = (
  location: TripLocation,
  homeAddress?: string,
  worksiteAddress?: string
): AutocompleteResult => {
  const address = location.home
    ? homeAddress || 'Home'
    : location.worksite
      ? worksiteAddress || 'Work'
      : location.lat != null && location.lng != null
        ? `${location.lat}, ${location.lng}`
        : ''
  return {
    address,
    coordinates:
      location.lng != null && location.lat != null
        ? [location.lng, location.lat]
        : [],
    postcode: null,
    type: location.home ? 'HOME' : location.worksite ? 'WORK' : undefined,
  }
}

export const initialTripFormValues = (
  locationState: TripFormLocationState | null,
  homeAddress?: string,
  worksiteAddress?: string
) => {
  const today = moment()
  const trip = locationState?.trip
  const tripDate = trip ? moment(trip.date) : null
  const source = trip?.source
    ? savedTripLocation(trip.source, homeAddress, worksiteAddress)
    : null
  const dest = trip?.dest
    ? savedTripLocation(trip.dest, homeAddress, worksiteAddress)
    : null

  return {
    date: {
      day: String(
        tripDate ? tripDate.date() : locationState?.day || today.date()
      ),
      month: String(
        tripDate
          ? tripDate.month() + 1
          : locationState?.month
            ? locationState.month + 1
            : today.month() + 1
      ),
      year: String(
        tripDate ? tripDate.year() : locationState?.year || today.year()
      ),
    },
    timeRange: trip?.timeRange || null,
    source,
    sourceAddr: source?.address || null,
    dest,
    destAddr: dest?.address || null,
    purpose: trip?.purpose || null,
    modeIds: trip?.modeIds || [],
  }
}

export const buildTripPayload = (input: {
  date: TripDateParts
  timeRange: string
  source: AutocompleteResult
  dest: AutocompleteResult
  purpose: string | null
  modeIds: string[]
}): AddTripPayload => {
  return {
    date: moment(
      `${input.date.year}-${input.date.month}-${input.date.day}`
    ).format('YYYY-MM-DDThh:mm'),
    timeRange: input.timeRange,
    source: formatTripLocation(input.source),
    dest: formatTripLocation(input.dest),
    purpose: input.purpose,
    modeIds: input.modeIds,
    noTripReason: null,
  }
}

export const isTripSaveDisabled = (input: {
  date: TripDateParts
  timeRange: string | null
  source: AutocompleteResult | null
  dest: AutocompleteResult | null
  modeIds: string[]
}) => {
  return (
    isFutureDate(
      moment(`${input.date.year}-${input.date.month}-${input.date.day}`)
    ) ||
    !input.timeRange ||
    !input.source ||
    !input.dest ||
    input.modeIds.length === 0
  )
}
